/**
 * Khối compute: phân tán / biên độ (BBI, SAR, Keltner, Donchian, ChaikinVol,
 * StdDev, HV, CI, Supertrend).
 *
 * Một nhóm trong `computeIndicatorComputationResult`, tách riêng (split 2026-09-30).
 * Supertrend nằm ở đây vì nó là dải biên độ (ATR-based), không phải hệ thống xu hướng
 * — dù nó hay đi cùng DMA/Ichimoku trong UI.
 */
import { bbiSeries, chaikinVolSeries, choppinessIndexSeries, donchianSeries, historicalVolSeries, keltnerSeries, sarSeries, stdDevSeries, supertrendSeries, } from "../../indicators/utils";
import { blankUntil } from "./computationHelpers";
export function computeVolatilityBlocks(raw, closes, plan) {
    // CE15 indicators
    const bbi = plan.bbiEnabled
        ? bbiSeries(raw)
        : new Array(raw.length).fill(undefined);
    const sarByKey = new Map();
    for (const [key, config] of plan.sarByKey) {
        sarByKey.set(key, blankUntil(sarSeries(raw, config.afStep, config.afMax), 1));
    }
    const keltnerByKey = new Map();
    for (const [key, config] of plan.keltnerConfigs) {
        const series = keltnerSeries(raw, config.period, config.atrPeriod, config.multiplier);
        keltnerByKey.set(key, raw.map((_, index) => {
            const up = series.upper[index];
            const mid = series.middle[index];
            const low = series.lower[index];
            if (isNaN(mid)) {
                return undefined;
            }
            return { upper: up, middle: mid, lower: low };
        }));
    }
    const donchianByPeriod = new Map();
    for (const period of plan.donchianPeriods) {
        const series = donchianSeries(raw, period);
        donchianByPeriod.set(period, raw.map((_, index) => {
            const up = series.upper[index];
            const mid = series.middle[index];
            const low = series.lower[index];
            if (isNaN(mid)) {
                return undefined;
            }
            return { upper: up, middle: mid, lower: low };
        }));
    }
    const chaikinVolByPeriod = new Map();
    for (const period of plan.chaikinVolPeriods) {
        chaikinVolByPeriod.set(period, blankUntil(chaikinVolSeries(raw, period), period));
    }
    const stdDevByPeriod = new Map();
    for (const period of plan.stdDevPeriods) {
        stdDevByPeriod.set(period, blankUntil(stdDevSeries(closes, period), Math.max(period - 1, 0)));
    }
    const hvByKey = new Map();
    for (const [key, config] of plan.hvConfigs) {
        hvByKey.set(key, blankUntil(historicalVolSeries(closes, config.period, config.annualize), Math.max(config.period, 1)));
    }
    const ciByPeriod = new Map();
    for (const period of plan.ciPeriods) {
        ciByPeriod.set(period, blankUntil(choppinessIndexSeries(raw, period), Math.max(period - 1, 0)));
    }
    const supertrendByKey = new Map();
    for (const [key, config] of plan.supertrendConfigs) {
        const series = supertrendSeries(raw, config.period, config.multiplier);
        supertrendByKey.set(key, raw.map((_, index) => {
            const st = series.supertrend[index];
            const dir = series.direction[index];
            const ub = series.upperBand[index];
            const lb = series.lowerBand[index];
            if (isNaN(st)) {
                return undefined;
            }
            return {
                supertrend: st,
                direction: dir,
                upperBand: ub,
                lowerBand: lb,
            };
        }));
    }
    return {
        bbi,
        sarByKey,
        keltnerByKey,
        donchianByPeriod,
        chaikinVolByPeriod,
        stdDevByPeriod,
        hvByKey,
        ciByPeriod,
        supertrendByKey,
    };
}
